import { getStoryCategoryData } from "@/utils/storyCategorization";
import { getStorySection, getStorySectionLabel } from "@/utils/storySections";

const includesAny = (value, keywords) => keywords.some((keyword) => value.includes(keyword));

export const getStoryHaystack = (story) => `${story?.headline || ""} ${story?.excerpt || ""} ${story?.caption || ""} ${story?.location || ""} ${story?.ai_story || ""} ${(story?.tags || []).join(" ")}`.toLowerCase();

const isCrimeStory = (story) => {
  const haystack = getStoryHaystack(story);
  const { sceneCategory } = getStoryCategoryData(story);
  return [
    "Officer-Involved Shootings",
    "Road Rage Shootings",
    "Shootings",
    "Homicide Investigations",
    "SWAT & Standoffs",
    "Armed Robberies",
    "Robberies",
    "Stabbings",
    "School Threats",
    "Missing & Endangered Persons",
  ].includes(sceneCategory) || includesAny(haystack, ["crime", "arrest", "charged", "suspect", "shooting", "robbery", "murder"]);
};

const isPublicSafetyStory = (story) => {
  const haystack = getStoryHaystack(story);
  const { sceneCategory } = getStoryCategoryData(story);
  return [
    "Police Activity",
    "House Fires",
    "Fires",
    "Explosions",
    "Hazmat Incidents",
    "Flooding",
    "Storm Damage",
    "Weather Alerts",
    "Police Chases",
  ].includes(sceneCategory) || includesAny(haystack, ["public safety", "sheriff", "fire department", "emergency", "rescue", "evacuation"]);
};

const isPoliticsStory = (story) => includesAny(getStoryHaystack(story), ["mayor", "city council", "election", "vote", "campaign", "legislature", "politic", "government", "senate", "representative"]);

const isInvestigationStory = (story) => includesAny(getStoryHaystack(story), ["investigation", "investigates", "uncensored", "fraud", "corruption", "accountability", "waste", "abuse", "audit", "whistleblower"]);

const isRecordsStory = (story) => includesAny(getStoryHaystack(story), ["public records", "records request", "court records", "bodycam", "documents", "affidavit", "filing", "transparency", "report", "database"]);

const isCommunityStory = (story) => {
  const section = getStorySection(story);
  return section === "community" || section === "editorial" || includesAny(getStoryHaystack(story), ["community", "neighborhood", "school", "church", "festival", "business", "family", "resident"]);
};

const isWeatherStory = (story) => ["Flooding", "Storm Damage", "Weather Alerts"].includes(getStoryCategoryData(story).sceneCategory) || getStorySection(story) === "weather";
const isTrafficStory = (story) => ["Pedestrian Crashes", "Hit-and-Runs", "Motorcycle Crashes", "18-Wheeler Crashes", "Deadly Car Crashes in Houston", "Car Crashes", "Police Chases"].includes(getStoryCategoryData(story).sceneCategory) || includesAny(getStoryHaystack(story), ["traffic", "freeway", "lane closure", "road closure"]);
const isVideoStory = (story) => Boolean(story?.video_url || story?.permalink) || ["video", "short"].includes(story?.content_type);

export const NEWSROOM_SECTION_CONFIG = {
  crime: {
    key: "crime",
    route: "/crime",
    title: "Crime",
    overline: "Houston crime desk",
    description: "Breaking crime coverage, police investigations, arrests, and active scenes across Houston and Southeast Texas.",
    matches: (story) => getStorySection(story) === "crime" || isCrimeStory(story),
  },
  "public-safety": {
    key: "public-safety",
    route: "/public-safety",
    title: "Public Safety",
    overline: "Public safety desk",
    description: "Emergency response, fire scenes, law enforcement activity, severe weather response, and fast-moving safety alerts.",
    matches: (story) => getStorySection(story) === "public-safety" || isPublicSafetyStory(story),
  },
  politics: {
    key: "politics",
    route: "/politics",
    title: "Politics",
    overline: "Politics desk",
    description: "Houston and Texas political coverage, elections, city hall reporting, and accountability updates from public officials.",
    matches: (story) => getStorySection(story) === "politics" || isPoliticsStory(story),
  },
  investigations: {
    key: "investigations",
    route: "/investigations",
    title: "Investigations",
    overline: "Investigations desk",
    description: "Original reporting focused on corruption, accountability, fraud, waste, and public-interest investigations.",
    matches: (story) => getStorySection(story) === "investigations" || isInvestigationStory(story),
  },
  "public-records": {
    key: "public-records",
    route: "/public-records",
    title: "Public Records",
    overline: "Public records desk",
    description: "Documents, filings, court records, data-driven reporting, and transparency coverage across the Houston region.",
    matches: (story) => getStorySection(story) === "public-records" || isRecordsStory(story),
  },
  community: {
    key: "community",
    route: "/community",
    title: "Community",
    overline: "Community desk",
    description: "Neighborhood coverage, schools, civic issues, local events, and stories impacting Houston communities.",
    matches: (story) => isCommunityStory(story),
  },
  weather: {
    key: "weather",
    route: "/weather",
    title: "Weather",
    overline: "Weather desk",
    description: "Storm alerts, flood concerns, damage reports, and weather-driven disruptions across Houston and nearby counties.",
    matches: (story) => isWeatherStory(story),
  },
  traffic: {
    key: "traffic",
    route: "/traffic",
    title: "Traffic",
    overline: "Traffic desk",
    description: "Crashes, freeway disruptions, pursuits, lane closures, and commute alerts across Houston highways and streets.",
    matches: (story) => isTrafficStory(story),
  },
  videos: {
    key: "videos",
    route: "/videos",
    title: "Videos",
    overline: "Video reports",
    description: "Watch the latest video reports, shorts, livestream clips, and visual coverage from the Content Watch HQ newsroom.",
    matches: (story) => isVideoStory(story),
  },
};

export const MAIN_NAV_ITEMS = [
  { label: "Home", to: "/" },
  { label: "Breaking News", to: "/breaking-news" },
  { label: "Crime", to: "/crime" },
  { label: "Public Safety", to: "/public-safety" },
  { label: "Politics", to: "/politics" },
  { label: "Investigations", to: "/investigations" },
  { label: "Public Records", to: "/public-records" },
  { label: "Community", to: "/community" },
  { label: "Weather", to: "/weather" },
  { label: "Traffic", to: "/traffic" },
  { label: "Videos", to: "/videos" },
  { label: "Live", to: "/live" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
  { label: "Submit a Tip", to: "/submit-a-tip" },
];

export const LOCATION_HUB_CONFIG = {
  "houston-news": {
    key: "houston-news",
    route: "/houston-news",
    title: "Houston News",
    description: "A central Houston news hub pulling together breaking updates, politics, investigations, traffic, weather, and community coverage.",
    keywords: ["houston", "harris county"],
    liveVerified: true,
  },
  "harris-county-news": {
    key: "harris-county-news",
    route: "/harris-county-news",
    title: "Harris County News",
    description: "Coverage tied to Harris County government, public safety, courts, infrastructure, and countywide breaking updates.",
    keywords: ["harris county", "harris co", "houston"],
    liveVerified: false,
  },
  "fort-bend-county-news": {
    key: "fort-bend-county-news",
    route: "/fort-bend-county-news",
    title: "Fort Bend County News",
    description: "Regional coverage for Fort Bend County communities, public safety, politics, and quality-of-life stories.",
    keywords: ["fort bend", "sugar land", "rosenberg", "missouri city"],
    liveVerified: false,
  },
  "montgomery-county-news": {
    key: "montgomery-county-news",
    route: "/montgomery-county-news",
    title: "Montgomery County News",
    description: "Montgomery County alerts, local government coverage, investigations, and breaking news from north of Houston.",
    keywords: ["montgomery county", "conroe", "the woodlands", "spring"],
    liveVerified: false,
  },
  "galveston-county-news": {
    key: "galveston-county-news",
    route: "/galveston-county-news",
    title: "Galveston County News",
    description: "Breaking and enterprise coverage from Galveston County, coastal communities, weather impacts, and public safety scenes.",
    keywords: ["galveston", "league city", "galveston county"],
    liveVerified: false,
  },
  "texas-news": {
    key: "texas-news",
    route: "/texas-news",
    title: "Texas News",
    description: "Wider Texas coverage connecting Houston with statewide politics, investigations, emergency response, and major developments.",
    keywords: ["texas", "austin", "dallas", "san antonio", "beaumont"],
    liveVerified: false,
  },
};

export const LOCATION_HUB_LIST = Object.values(LOCATION_HUB_CONFIG);

export const getNewsroomStories = (feed, key, limit) => {
  const config = NEWSROOM_SECTION_CONFIG[key];
  if (!config) {
    return [];
  }
  const stories = (feed || []).filter((story) => config.matches(story));
  return typeof limit === "number" ? stories.slice(0, limit) : stories;
};

export const getLocationHubStories = (feed, key, limit) => {
  const config = LOCATION_HUB_CONFIG[key];
  if (!config || config.liveVerified === false) {
    return [];
  }
  const stories = (feed || []).filter((story) => includesAny(getStoryHaystack(story), config.keywords));
  return typeof limit === "number" ? stories.slice(0, limit) : stories;
};

export const getLatestStories = (feed, limit = 6) => (feed || []).slice(0, limit);

export const getTopStoryGroups = (feed) => ({
  breaking: getLatestStories((feed || []).filter((story) => getStorySection(story) === "breaking"), 1)[0],
  latest: getLatestStories(feed, 6),
  crime: getNewsroomStories(feed, "crime", 4),
  publicSafety: getNewsroomStories(feed, "public-safety", 4),
  investigations: getNewsroomStories(feed, "investigations", 4),
  politics: getNewsroomStories(feed, "politics", 4),
  community: getNewsroomStories(feed, "community", 4),
  publicRecords: getNewsroomStories(feed, "public-records", 4),
  videos: getNewsroomStories(feed, "videos", 4),
});

export const getBreadcrumbItems = (label, route) => [
  { label: "Home", to: "/" },
  { label, to: route },
];

export const getSectionBadge = (story) => getStorySectionLabel(getStorySection(story));